import { useTranslation } from 'react-i18next';
import { Wallet, TrendingUp, Eye, MousePointer } from 'lucide-react';

import { cn, formatPrice, getAmountByCurrency } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useCampaigns } from '../../hooks/use-campaigns';
import { useAdWallet } from '../../hooks/use-ad-wallet';
import { Skeleton } from '@/components/atoms/skeleton';

interface StatItemProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

function StatItem({ label, value, icon, onClick }: StatItemProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1 min-w-[80px] p-3 rounded-lg bg-muted/50',
        onClick && 'cursor-pointer active:bg-muted'
      )}
    >
      <div className="text-muted-foreground">{icon}</div>
      <span className="text-lg font-bold">{value}</span>
      <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
    </div>
  );
}

interface CompactStatsProps {
  onWalletClick?: () => void;
}

export function CompactStats({ onWalletClick }: CompactStatsProps) {
  const { t } = useTranslation();
  const { currency } = useConfigStore();

  const { data: campaignsData, isLoading: campaignsLoading } = useCampaigns();
  const { data: wallet, isLoading: walletLoading } = useAdWallet();

  const isLoading = campaignsLoading || walletLoading;

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-col items-center gap-1 min-w-[80px] p-3 rounded-lg bg-muted/50">
            <Skeleton className="size-4" />
            <Skeleton className="h-6 w-12" />
            <Skeleton className="h-3 w-14" />
          </div>
        ))}
      </div>
    );
  }

  const campaigns = campaignsData?.campaigns ?? [];
  const totalSpent = campaigns.reduce(
    (sum, c) =>
      sum + getAmountByCurrency(currency, { eur: c.spentEur, usd: c.spentUsd, kmf: c.spentKmf }),
    0
  );
  const totalImpressions = campaigns.reduce((sum, c) => sum + (c.impressions ?? 0), 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + (c.clicks ?? 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  const walletBalance = getAmountByCurrency(currency, {
    eur: wallet?.balanceEur,
    usd: wallet?.balanceUsd,
    kmf: wallet?.balanceKmf,
  });

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
      <StatItem
        label={t('ads.adBalance', 'Solde')}
        value={formatPrice(walletBalance, currency)}
        icon={<Wallet className="size-4" />}
        onClick={onWalletClick}
      />
      <StatItem
        label={t('ads.totalSpent', 'Dépensé')}
        value={formatPrice(totalSpent, currency)}
        icon={<TrendingUp className="size-4" />}
      />
      <StatItem
        label={t('ads.impressions', 'Impress.')}
        value={totalImpressions >= 1000 ? `${(totalImpressions / 1000).toFixed(1)}K` : String(totalImpressions)}
        icon={<Eye className="size-4" />}
      />
      <StatItem
        label={t('ads.ctr', 'CTR')}
        value={`${avgCtr.toFixed(1)}%`}
        icon={<MousePointer className="size-4" />}
      />
    </div>
  );
}
